import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { Quiz, QuizAttempt, SectionFilter } from '@/types';

interface QuizState {
  quizzes: Quiz[];
  currentQuiz: Quiz | null;
  currentAnswers: (number | null)[];
  currentQuestionIndex: number;
  attempts: QuizAttempt[];
  isLoading: boolean;
  error: string | null;
  setQuizzes: (quizzes: Quiz[]) => void;
  setCurrentQuiz: (quiz: Quiz | null) => void;
  answerQuestion: (index: number, answer: number) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  fetchQuizzes: (userId: string) => Promise<void>;
  generateQuiz: (documentIds: string[], userId: string, count: number, sectionFilters?: SectionFilter[]) => Promise<Quiz | null>;
  submitQuiz: (userId: string) => Promise<QuizAttempt | null>;
  fetchAttempts: (quizId: string) => Promise<void>;
  deleteQuiz: (quizId: string) => Promise<void>;
}

export const useQuizStore = create<QuizState>((set, get) => ({
  quizzes: [],
  currentQuiz: null,
  currentAnswers: [],
  currentQuestionIndex: 0,
  attempts: [],
  isLoading: false,
  error: null,

  setQuizzes: (quizzes) => set({ quizzes }),
  setCurrentQuiz: (quiz) => set({
    currentQuiz: quiz,
    currentAnswers: quiz ? Array(quiz.questions.length).fill(null) : [],
    currentQuestionIndex: 0,
  }),
  answerQuestion: (index, answer) => set((state) => {
    const newAnswers = [...state.currentAnswers];
    newAnswers[index] = answer;
    return { currentAnswers: newAnswers };
  }),
  nextQuestion: () => set((state) => ({
    currentQuestionIndex: Math.min(
      state.currentQuestionIndex + 1,
      (state.currentQuiz?.questions.length || 1) - 1
    ),
  })),
  previousQuestion: () => set((state) => ({
    currentQuestionIndex: Math.max(state.currentQuestionIndex - 1, 0),
  })),

  fetchQuizzes: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchQuizzes');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ quizzes: data || [], isLoading: false });
  },

  generateQuiz: async (documentIds: string[], userId: string, count: number, sectionFilters?: SectionFilter[]) => {
    Sentry.addBreadcrumb({ category: 'quiz', message: `Generation started: count=${count}`, level: 'info' });
    return Sentry.startSpan(
      { name: 'quiz.generate', op: 'ai.generation', attributes: { 'generation.count': count, 'documents.count': documentIds.length } },
      async () => {
    set({ isLoading: true, error: null });

    // 1. Create quiz row with status: 'generating'
    const { data: newQuiz, error: insertError } = await supabase
      .from('quizzes')
      .insert({
        user_id: userId,
        title: 'Generating quiz...',
        document_ids: documentIds,
        questions: [],
        status: 'generating',
      })
      .select()
      .single();

    if (insertError || !newQuiz) {
      set({ error: insertError?.message || 'Failed to create quiz', isLoading: false });
      return null;
    }

    // 2. Add to local state immediately
    set((state) => ({
      quizzes: [newQuiz, ...state.quizzes],
      isLoading: false,
    }));

    // 3. Fire-and-forget edge function
    supabase.functions.invoke('generate-quiz', {
      body: { quizId: newQuiz.id, documentIds, count, sectionFilters },
    }).catch((err: unknown) => {
      Sentry.captureException(err, { tags: { context: 'quizStore.generateQuiz' } });
    });

    return newQuiz;
      });
  },

  submitQuiz: async (userId: string) => {
    const { currentQuiz, currentAnswers } = get();
    if (!currentQuiz) return null;

    // Calculate score
    let correct = 0;
    currentQuiz.questions.forEach((q, i) => {
      if (currentAnswers[i] === q.correct_index) correct++;
    });
    const score = Math.round((correct / currentQuiz.questions.length) * 100);

    const attempt: Omit<QuizAttempt, 'id'> = {
      quiz_id: currentQuiz.id,
      user_id: userId,
      answers: currentAnswers.map((a) => a ?? -1),
      score,
      completed_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('quiz_attempts')
      .insert(attempt)
      .select()
      .single();

    if (error) {
      reportSupabaseError(error, 'quizStore.submitQuiz');
      set({ error: error.message });
      return null;
    }

    set((state) => ({
      attempts: [data, ...state.attempts],
    }));

    return data;
  },

  fetchAttempts: async (quizId: string) => {
    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('quiz_id', quizId)
      .order('completed_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'quizStore.fetchAttempts');
      set({ error: error.message });
      return;
    }

    set({ attempts: data || [] });
  },

  deleteQuiz: async (quizId: string) => {
    const { error } = await supabase
      .from('quizzes')
      .delete()
      .eq('id', quizId);

    if (error) {
      reportSupabaseError(error, 'quizStore.deleteQuiz');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      quizzes: state.quizzes.filter((q) => q.id !== quizId),
      currentQuiz: state.currentQuiz?.id === quizId ? null : state.currentQuiz,
    }));
  },
}));
